import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { playerService } from '../../../services/performanceService';

const PlayerPerformanceForm = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    // Personal Information
    fullName: '',
    age: '',
    dateOfBirth: '',
    nationality: '',
    height: '',
    position: '',
    preferredFoot: '',
    // Club Career
    previousTeams: '',
    currentTeam: '',
    competitions: '',
    totalAppearances: '',
    gamesStarted: '',
    gamesSubstitute: '',
    totalMinutes: '',
    // Performance Statistics
    goalsScored: '',
    assists: '',
    motmAwards: '',
    yellowCards: '',
    redCards: '',
    // Transfer & Market Data
    availableForTransfer: false,
    marketValue: '',
    contractStatus: '',
    releaseClause: '',
    // Agent Information
    agentName: '',
    agentEmail: '',
    agentPhone: '',
    // International Career
    nationalTeam: '',
    juniorLevel: '',
    seniorTeam: '',
    internationalCaps: '',
    internationalGoals: '',
    // Media
    playerPhoto: null,
    matchFootage: null,
    medicalReports: null
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const positionOptions = [
    { value: 'forward', label: 'Forward' },
    { value: 'midfielder', label: 'Midfielder' },
    { value: 'defender', label: 'Defender' },
    { value: 'winger', label: 'Winger' },
    { value: 'striker', label: 'Striker' },
    { value: 'attacking-midfielder', label: 'Attacking Midfielder' },
    { value: 'defensive-midfielder', label: 'Defensive Midfielder' },
    { value: 'center-back', label: 'Center Back' },
    { value: 'full-back', label: 'Full Back' }
  ];

  const footOptions = [
    { value: 'right', label: 'Right' },
    { value: 'left', label: 'Left' },
    { value: 'both', label: 'Both' }
  ];

  const contractStatusOptions = [
    { value: 'active', label: 'Active Contract' },
    { value: 'expiring', label: 'Expiring Soon' },
    { value: 'free-agent', label: 'Free Agent' },
    { value: 'loan', label: 'On Loan' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'file' ? files?.[0] : value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.fullName?.trim()) newErrors.fullName = 'Full name is required';
    if (!formData?.age) newErrors.age = 'Age is required';
    if (!formData?.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
    if (!formData?.nationality?.trim()) newErrors.nationality = 'Nationality is required';
    if (!formData?.height?.trim()) newErrors.height = 'Height is required';
    if (!formData?.position) newErrors.position = 'Position is required';
    if (!formData?.preferredFoot) newErrors.preferredFoot = 'Preferred foot is required';
    if (!formData?.currentTeam?.trim()) newErrors.currentTeam = 'Current team is required';
    if (!formData?.agentEmail?.trim()) {
      newErrors.agentEmail = 'Agent email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.agentEmail)) {
      newErrors.agentEmail = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Prepare files object
      const files = {
        playerPhoto: formData?.playerPhoto,
        matchFootage: formData?.matchFootage,
        medicalReports: formData?.medicalReports
      };
      
      // Prepare data object (exclude file objects)
      const playerData = {
        fullName: formData?.fullName,
        age: parseInt(formData?.age),
        dateOfBirth: formData?.dateOfBirth,
        nationality: formData?.nationality,
        height: formData?.height,
        position: formData?.position,
        preferredFoot: formData?.preferredFoot,
        previousTeams: formData?.previousTeams,
        currentTeam: formData?.currentTeam,
        competitions: formData?.competitions,
        totalAppearances: parseInt(formData?.totalAppearances) || 0,
        gamesStarted: parseInt(formData?.gamesStarted) || 0,
        gamesSubstitute: parseInt(formData?.gamesSubstitute) || 0,
        totalMinutes: parseInt(formData?.totalMinutes) || 0,
        goalsScored: parseInt(formData?.goalsScored) || 0,
        assists: parseInt(formData?.assists) || 0,
        motmAwards: parseInt(formData?.motmAwards) || 0,
        yellowCards: parseInt(formData?.yellowCards) || 0,
        redCards: parseInt(formData?.redCards) || 0,
        availableForTransfer: formData?.availableForTransfer,
        marketValue: formData?.marketValue,
        contractStatus: formData?.contractStatus,
        releaseClause: formData?.releaseClause,
        agentName: formData?.agentName,
        agentEmail: formData?.agentEmail,
        agentPhone: formData?.agentPhone,
        nationalTeam: formData?.nationalTeam,
        juniorLevel: formData?.juniorLevel,
        seniorTeam: formData?.seniorTeam,
        internationalCaps: parseInt(formData?.internationalCaps) || 0,
        internationalGoals: parseInt(formData?.internationalGoals) || 0
      };
      
      const { data, error } = await playerService?.create(playerData, files);
      
      if (error) {
        console.error('Error creating player record:', error);
        setErrors({ submit: error?.message || 'Failed to submit player record' });
        return;
      }
      
      onSubmit?.();
      
      // Reset form
      setFormData({
        fullName: '', age: '', dateOfBirth: '', nationality: '', height: '', position: '',
        preferredFoot: '', previousTeams: '', currentTeam: '', competitions: '',
        totalAppearances: '', gamesStarted: '', gamesSubstitute: '', totalMinutes: '',
        goalsScored: '', assists: '', motmAwards: '', yellowCards: '', redCards: '',
        availableForTransfer: false, marketValue: '', contractStatus: '', releaseClause: '',
        agentName: '', agentEmail: '', agentPhone: '', nationalTeam: '', juniorLevel: '',
        seniorTeam: '', internationalCaps: '', internationalGoals: '',
        playerPhoto: null, matchFootage: null, medicalReports: null
      });
    } catch (error) {
      console.error('Submit error:', error);
      setErrors({ submit: 'An unexpected error occurred' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-8">
      {/* Player Profile Information */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="User" size={20} className="text-primary" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">Player Profile Information</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Full Name"
            name="fullName"
            value={formData?.fullName}
            onChange={handleChange}
            placeholder="Enter full name"
            required
            error={errors?.fullName}
          />
          <Input
            label="Age"
            name="age"
            type="number"
            value={formData?.age}
            onChange={handleChange}
            placeholder="Enter age"
            required
            error={errors?.age}
          />
          <Input
            label="Date of Birth"
            name="dateOfBirth"
            type="date"
            value={formData?.dateOfBirth}
            onChange={handleChange}
            required
            error={errors?.dateOfBirth}
          />
          <Input
            label="Nationality"
            name="nationality"
            value={formData?.nationality}
            onChange={handleChange}
            placeholder="Enter nationality"
            required
            error={errors?.nationality}
          />
          <Input
            label="Height"
            name="height"
            value={formData?.height}
            onChange={handleChange}
            placeholder="e.g., 1.82m or 6'0&quot;"
            required
            error={errors?.height}
          />
          <Select
            label="Position"
            name="position"
            options={positionOptions}
            value={formData?.position}
            onChange={(value) => handleSelectChange('position', value)}
            placeholder="Select position"
            required
            error={errors?.position}
          />
          <Select
            label="Preferred Foot"
            name="preferredFoot"
            options={footOptions}
            value={formData?.preferredFoot}
            onChange={(value) => handleSelectChange('preferredFoot', value)}
            placeholder="Select preferred foot"
            required
            error={errors?.preferredFoot}
          />
        </div>
      </div>
      {/* Club Career History */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="Building2" size={20} className="text-primary" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">Club Career History</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Previous Teams"
            name="previousTeams"
            value={formData?.previousTeams}
            onChange={handleChange}
            placeholder="e.g., Team A (2020-2022), Team B (2018-2020)"
            description="List previous teams with years"
          />
          <Input
            label="Current Team"
            name="currentTeam"
            value={formData?.currentTeam}
            onChange={handleChange}
            placeholder="Enter current team"
            required
            error={errors?.currentTeam}
          />
          <Input
            label="Competitions Played"
            name="competitions"
            value={formData?.competitions}
            onChange={handleChange}
            placeholder="e.g., Premier League, Champions League"
          />
          <Input
            label="Total Appearances"
            name="totalAppearances"
            type="number"
            value={formData?.totalAppearances}
            onChange={handleChange}
            placeholder="Total games played"
          />
          <Input
            label="Games Started"
            name="gamesStarted"
            type="number"
            value={formData?.gamesStarted}
            onChange={handleChange}
            placeholder="Games started"
          />
          <Input
            label="Games as Substitute"
            name="gamesSubstitute"
            type="number"
            value={formData?.gamesSubstitute}
            onChange={handleChange}
            placeholder="Games played as substitute"
          />
          <Input
            label="Total Minutes Played"
            name="totalMinutes"
            type="number"
            value={formData?.totalMinutes}
            onChange={handleChange}
            placeholder="Total minutes on pitch"
          />
        </div>
      </div>
      {/* Performance Statistics */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="BarChart3" size={20} className="text-primary" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">Performance Statistics</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Input
            label="Goals Scored"
            name="goalsScored"
            type="number"
            value={formData?.goalsScored}
            onChange={handleChange}
            placeholder="Total goals"
          />
          <Input
            label="Assists"
            name="assists"
            type="number"
            value={formData?.assists}
            onChange={handleChange}
            placeholder="Total assists"
          />
          <Input
            label="Man of the Match Awards"
            name="motmAwards"
            type="number"
            value={formData?.motmAwards}
            onChange={handleChange}
            placeholder="MOTM awards"
          />
          <Input
            label="Yellow Cards"
            name="yellowCards"
            type="number"
            value={formData?.yellowCards}
            onChange={handleChange}
            placeholder="Yellow cards"
            description="Optional disciplinary record"
          />
          <Input
            label="Red Cards"
            name="redCards"
            type="number"
            value={formData?.redCards}
            onChange={handleChange}
            placeholder="Red cards"
            description="Optional disciplinary record"
          />
        </div>
      </div>
      {/* Transfer & Market Data */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="TrendingUp" size={20} className="text-primary" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">Transfer & Market Data</h2>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
            <Checkbox
              id="availableForTransfer"
              checked={formData?.availableForTransfer}
              onCheckedChange={(checked) => handleSelectChange('availableForTransfer', checked)}
            />
            <label htmlFor="availableForTransfer" className="text-sm font-medium cursor-pointer">
              Available for Transfer
            </label>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Estimated Market Value"
              name="marketValue"
              value={formData?.marketValue}
              onChange={handleChange}
              placeholder="e.g., €5.0M"
            />
            <Select
              label="Contract Status"
              name="contractStatus"
              options={contractStatusOptions}
              value={formData?.contractStatus}
              onChange={(value) => handleSelectChange('contractStatus', value)}
              placeholder="Select contract status"
            />
            <Input
              label="Release Clause"
              name="releaseClause"
              value={formData?.releaseClause}
              onChange={handleChange}
              placeholder="e.g., €10.0M"
              description="Optional"
            />
          </div>
        </div>
      </div>
      {/* Agent Information */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="Briefcase" size={20} className="text-primary" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">Agent Information</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Agent Name"
            name="agentName"
            value={formData?.agentName}
            onChange={handleChange}
            placeholder="Enter agent name"
          />
          <Input
            label="Agent Email"
            name="agentEmail"
            type="email"
            value={formData?.agentEmail}
            onChange={handleChange}
            placeholder="agent@example.com"
            required
            error={errors?.agentEmail}
          />
          <Input
            label="Agent Phone"
            name="agentPhone"
            type="tel"
            value={formData?.agentPhone}
            onChange={handleChange}
            placeholder="+44 123 456 7890"
            description="Optional"
          />
        </div>
      </div>
      {/* International Career */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="Flag" size={20} className="text-primary" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">International Career</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="National Team Representation"
            name="nationalTeam"
            value={formData?.nationalTeam}
            onChange={handleChange}
            placeholder="e.g., Nigeria"
          />
          <Input
            label="Junior Level"
            name="juniorLevel"
            value={formData?.juniorLevel}
            onChange={handleChange}
            placeholder="e.g., U-17, U-20, U-23"
          />
          <Input
            label="Senior National Team"
            name="seniorTeam"
            value={formData?.seniorTeam}
            onChange={handleChange}
            placeholder="Senior team name"
          />
          <Input
            label="International Caps"
            name="internationalCaps"
            type="number"
            value={formData?.internationalCaps}
            onChange={handleChange}
            placeholder="Number of caps"
          />
          <Input
            label="International Goals"
            name="internationalGoals"
            type="number"
            value={formData?.internationalGoals}
            onChange={handleChange}
            placeholder="Goals for national team"
          />
        </div>
      </div>
      {/* Media & Documents */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="Upload" size={20} className="text-primary" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">Media & Documents</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Player Photo"
            name="playerPhoto"
            type="file"
            onChange={handleChange}
            description="Upload professional photo"
          />
          <Input
            label="Match Footage"
            name="matchFootage"
            type="file"
            onChange={handleChange}
            description="Optional video highlights"
          />
          <Input
            label="Medical/Fitness Reports"
            name="medicalReports"
            type="file"
            onChange={handleChange}
            description="Optional medical documents"
          />
        </div>
      </div>
      {/* Submit Button */}
      <div className="flex justify-end gap-4 pt-6 border-t border-border">
        <Button
          type="button"
          variant="outline"
          onClick={() => window.location?.reload()}
        >
          Reset Form
        </Button>
        <Button
          type="submit"
          loading={isSubmitting}
          iconName="Save"
        >
          {isSubmitting ? 'Submitting...' : 'Submit Player Record'}
        </Button>
      </div>
    </form>
  );
};

export default PlayerPerformanceForm;